import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/site/Seo";
import Band, { Eyebrow } from "@/components/site/Band";
import { OG } from "@/lib/content";

const AREAS = [
  ["Credentialing and payer enrollment", "We enroll your providers with payers and keep credentials current, so claims don't bounce on a lapsed enrollment."],
  ["Billing and revenue cycle", "Claims, follow-up, denials, and posting — run as a service, not added to your staff."],
  ["Staffing and training", "The people who run enrollment, device support, and documentation are ours, trained on your program."],
  ["Compliance program and documentation", "Policies, consent capture, and the audit file, maintained continuously rather than reconstructed under pressure."],
  ["Technology and EHR integration", "We connect to your EHR so data and documentation flow without double entry."],
  ["Reporting", "Plain reporting on enrollment, billing months, and program performance — the numbers you'd want before a partner meeting."],
];

export default function Mso() {
  return (
    <>
      <Seo
        title="Administrative services for practices running 340B or RPM"
        description="You keep clinical control. We handle credentialing, billing, staffing, compliance, EHR integration, and reporting so a 340B or RPM program is survivable without new hires."
        path="/mso"
        ogImage={OG.mso}
      />

      <Band tone="spruce">
        <Eyebrow>Administrative services</Eyebrow>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.03] mb-6 max-w-3xl">
          You keep clinical control. We handle the rest.
        </h1>
        <p className="text-bone/85 text-base sm:text-lg max-w-2xl">
          A 340B or RPM program only works if someone runs it. This is that someone. It's the answer to
          the objection every practice has: this sounds like work I don't have time for.
        </p>
      </Band>

      <Band tone="bone">
        <Eyebrow>What we run</Eyebrow>
        <div className="grid sm:grid-cols-2 gap-px bg-sage/40 border border-sage/40 rounded-[2px] overflow-hidden">
          {AREAS.map(([t, d], i) => (
            <div key={i} className="bg-bone p-6 sm:p-8" data-testid={`mso-area-${i}`}>
              <div className="tabular text-brass text-sm mb-2">{String(i + 1).padStart(2, "0")}</div>
              <h3 className="font-display text-2xl text-spruce mb-2">{t}</h3>
              <p className="text-sm text-ink/80 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </Band>

      {/* The boundary — explicit */}
      <Band tone="ink">
        <Eyebrow>The boundary, stated plainly</Eyebrow>
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl">
          <div className="border-t border-brass pt-5" data-testid="mso-boundary-practice">
            <h3 className="text-lg font-semibold mb-3">You keep</h3>
            <p className="text-sm text-sage leading-relaxed">
              Clinical authority. Every medical decision, every prescription, every treatment plan stays
              yours. Nothing here touches clinical judgment.
            </p>
          </div>
          <div className="border-t border-sage/40 pt-5" data-testid="mso-boundary-mso">
            <h3 className="text-lg font-semibold mb-3">We provide</h3>
            <p className="text-sm text-sage leading-relaxed">
              Administrative services only — the credentialing, billing, staffing, compliance, technology,
              and reporting that keep the program running.
            </p>
          </div>
        </div>
      </Band>

      <Band tone="bone">
        <div className="max-w-2xl">
          <h2 className="font-display text-3xl sm:text-4xl text-spruce leading-tight mb-4">
            Fees are a conversation, not a web page.
          </h2>
          <p className="text-base text-ink/80 mb-8">
            We don't publish fee structures. They depend on your program, your volume, and the covered
            entity you affiliate with. We'll walk you through it directly.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 text-sm font-semibold text-bone bg-spruce px-6 py-3 rounded-[2px] hover:bg-ink transition-colors"
            data-testid="mso-cta"
          >
            Talk through your program
            <ArrowRight size={16} />
          </Link>
        </div>
      </Band>
    </>
  );
}
